import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Quote } from 'lucide-react';
import { trackEvent } from '../hooks/useMetaPixel';
export function FinalCtaSection() {
  const testimonials = [{
    name: 'Camila R.',
    location: 'Chile 🇨🇱',
    initials: 'CR',
    avatarColor: 'from-pink-500 to-rose-500',
    quote: 'Literalmente no sabía nada de inteligencia artificial… y en menos de una semana ya había creado mi primer ebook. Todo está explicado súper simple. Es una locura lo que se puede hacer con esto.'
  }, {
    name: 'Daniela M.',
    location: 'México 🇲🇽',
    initials: 'DM',
    avatarColor: 'from-blue-500 to-cyan-500',
    quote: 'Lo que más me gustó es que no es solo teoría. Te dice exactamente qué hacer y con qué herramientas. Ya estoy creando contenido todos los días y me siento mucho más segura.'
  }, {
    name: 'Valentina G.',
    location: 'Colombia 🇨🇴',
    initials: 'VG',
    avatarColor: 'from-purple-500 to-indigo-500',
    quote: 'Sentía que estaba estancada, pero esta guía me abrió la mente. Empecé a aplicar lo que enseñan y ya tengo mis primeros ingresos online. Es demasiado potente.'
  }, {
    name: 'Paula S.',
    location: 'España 🇪🇸',
    initials: 'PS',
    avatarColor: 'from-emerald-500 to-teal-500',
    quote: 'Me encantó porque es paso a paso y no necesitas experiencia. Aprendí a usar varias herramientas de IA que ni sabía que existían.'
  }, {
    name: 'Fernanda L.',
    location: 'Chile 🇨🇱',
    initials: 'FL',
    avatarColor: 'from-amber-500 to-orange-500',
    quote: 'Lo compré sin muchas expectativas… y terminó siendo una de las mejores decisiones. Ya estoy creando productos digitales y entendiendo cómo venderlos.'
  }, {
    name: 'Sofía T.',
    location: 'Estados Unidos 🇺🇸',
    initials: 'ST',
    avatarColor: 'from-sky-500 to-blue-600',
    quote: 'Trabajo y tengo poco tiempo, pero esta guía me permitió empezar desde casa. Es práctica, directa y muy fácil de aplicar.'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-950 via-pink-950/30 to-[#0d0b14] relative overflow-hidden" data-id="element-41">
      {/* Decorative sparkles */}
      <div className="absolute top-10 left-10 text-pink-500/40 opacity-50 animate-pulse" data-id="element-42">
        <Sparkles className="w-12 h-12" data-id="element-43" />
      </div>
      <div className="absolute bottom-10 right-10 text-purple-500/40 opacity-50 animate-pulse delay-300" data-id="element-44">
        <Sparkles className="w-16 h-16" data-id="element-45" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-46">
        {/* Testimonials Section */}
        <div className="mb-24" data-id="element-47">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-48">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400 mb-4 inline-flex items-center gap-3" data-id="element-49">
              <span className="text-yellow-400 drop-shadow-md" data-id="element-50">⭐</span>
              Lo que dicen quienes ya descargaron la guía
            </h2>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-51">
            {testimonials.map((testimonial, index) => <motion.div key={index} variants={itemVariants} className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-xl hover:bg-white/[0.05] transition-colors duration-300 relative overflow-hidden group" data-id="element-52">
                {/* Decorative Quote Icon */}
                <div className="absolute top-6 right-6 text-white/5 group-hover:text-pink-500/10 transition-colors duration-300" data-id="element-53">
                  <Quote className="w-16 h-16 rotate-180" data-id="element-54" />
                </div>

                {/* Stars */}
                <div className="flex gap-1 mb-6 relative z-10" data-id="element-55">
                  {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-lg drop-shadow-sm" data-id="element-56">
                      ⭐
                    </span>)}
                </div>

                {/* Quote */}
                <p className="text-slate-300 italic text-lg leading-relaxed mb-8 relative z-10" data-id="element-57">
                  "{testimonial.quote}"
                </p>

                {/* User Info */}
                <div className="flex items-center gap-4 relative z-10 mt-auto" data-id="element-58">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${testimonial.avatarColor} flex items-center justify-center text-white font-bold text-lg shadow-lg`} data-id="element-59">
                    {testimonial.initials}
                  </div>
                  <div data-id="element-60">
                    <h4 className="text-white font-bold text-lg" data-id="element-61">
                      {testimonial.name}
                    </h4>
                    <p className="text-slate-400 text-sm" data-id="element-62">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Existing CTA Section */}
        <div className="max-w-4xl mx-auto text-center" data-id="element-63">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-64">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6" data-id="element-65">
              El momento perfecto para empezar no existe.
            </h2>

            <p className="text-xl sm:text-2xl text-slate-300 mb-12 max-w-2xl mx-auto" data-id="element-66">
              Pero sí existe el momento en que decides tomar una nueva
              dirección. <br className="hidden sm:block" data-id="element-67" />
              <span className="font-semibold text-pink-400" data-id="element-68">
                Hoy puede ser ese momento.
              </span>
            </p>

            <motion.a href="https://pay.hotmart.com/R104785188L" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('AddToCart')} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="px-8 py-4 bg-gradient-primary text-white rounded-full font-bold text-lg shadow-xl shadow-pink-500/40 hover:shadow-pink-500/60 transition-all inline-flex items-center justify-center gap-2 mx-auto" data-id="element-69">
              <span data-id="element-70">QUIERO VOLVER A BRILLAR</span>
              <Sparkles className="w-5 h-5" data-id="element-71" />
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>;
}